<template>
  <div>
    <div class="instance-name-field__container">
      <div class="instance-name-field__background"></div>
      <h4 class="instance-name-field__title">
        <slot name="instruction">
          Start by creating your{{ toolDisplayName }}instance:
        </slot>
      </h4>
      <template v-if="ui.instance.loading">
        <loading-spinner size="1x"></loading-spinner>
      </template>
      <template v-else>
        <p class="instance-name-field__input__title">
          Name:
          <span class="instance-name-field__asterix">*</span>
        </p>
        <b-form-input
          ref="instanceNameField"
          v-model="instanceName"
          :state="instanceNameValidationState"
          class="instance-name-field__input">
        </b-form-input>
        <b-form-invalid-feedback id="input-live-feedback">
          <template v-if="isNameTaken">
            The name {{ instanceName }} has already been used. Please, try another.
          </template>
          <template v-else>
            A name is required to continue
          </template>
        </b-form-invalid-feedback>
        <b-alert :show="ui.instance.error"
                 variant="danger"
                 class="alert">
          {{ ui.instance.errorMessage }}
        </b-alert>
      </template>
    </div>
    <prev-next :is-next-disabled.sync="ui.next.disabled"
               @next="onNext"
               @previous="onPrev">
    </prev-next>
  </div>
</template>

<script>
import {
  saveInstance as saveThreeSixtyInstance,
  listUserInstances as loadThreeSixtyInstances,
} from "~/Scripts/resources/threeSixtyResource";
import {
  saveInstance as savePersonaInstance,
  listUserInstances as loadPersonaInstances,
} from "~/Scripts/resources/personaResource";
import {
  saveInstance as saveStyleInstance,
  listUserInstances as loadStyleInstances,
} from "~/Scripts/resources/styleResource";
import {
  saveInstance as saveSurveyInstance,
  listUserInstances as loadSurveyInstances,
} from "~/Scripts/resources/surveyResource";

import {
  saveInstance as saveEngageInstance,
  listInstances as loadEngageInstances,
} from "~/Scripts/resources/engageResource";

import {
  saveInstance as saveTeamInstance,
  listInstances as loadTeamInstances,
} from "~/Scripts/resources/teamResource";

import PrevNext from "~/Components/Common/PrevNext.vue";
import { INSTANCE_TYPE } from "~/Components/constants";

export default {
  name: "InstanceNameField",
  components: {
    PrevNext,
  },
  props: {
    toolName: {
      type: String,
      default: "",
    },
    next: {
      type: String,
      default: undefined,
    },
    instanceNames: {
      type: Array,
      default: () => null,
    },
    instanceType: {
      type: String,
      required: true,
      // eslint-disable-next-line
      validator(val) {
        return [
          INSTANCE_TYPE.THREE_SIXTY,
          INSTANCE_TYPE.PERSONA,
          INSTANCE_TYPE.STYLE,
          INSTANCE_TYPE.SURVEY,
          INSTANCE_TYPE.ENGAGE,
          INSTANCE_TYPE.TEAM,
        ].includes( val.toUpperCase() );
      },
    },
    surveyType: {
      type: String,
      default: null,
      // eslint-disable-next-line
      validator(val) {
        return ["STAFF", "PUPIL", "PARENT"].includes( val );
      },
    },
  },
  data: () => ( {
    instanceName: "",
    instances: [],
    defaultSurveyExpiryDays: 10,
    userInputDetected: false,
    currentInstanceId: false,
    INSTANCE_TYPE,
    ui: {
      instance: {
        error: false,
        loading: false,
        errorMessage: "Uh oh! Something went wrong. Please, try again.",
      },
      next: {
        disabled: false,
      },
      validation: {
        nameTaken: false,
      },
    },
  } ),
  computed: {
    toolDisplayName () {
      return this.toolName ? ` ${this.toolName} ` : " ";
    },
    instanceNameValidationState () {
      const hasName = this.isNullOrEmpty( this.instanceName ) === false;
      const isUnique = this.isNameTaken === false;
      return this.userInputDetected ? hasName && isUnique : null;
    },
    isNameTaken () {
      return this.instances.includes( this.instanceName );
    },
  },
  watch: {
    instanceName: {
      handler ( val /* , oldVal */ ) {
        if ( this.userInputDetected === false ) {
          this.userInputDetected = true;
        }
        this.$emit( "instance-name", val );
      },
    },
  },
  async created () {
    if ( this.instanceNames ) {
      this.instanceNameList = this.instancesNames;
    } else {
      await this.loadInstanceNames();
    }
  },
  mounted () {
    this.focusInstanceNameField();
  },
  methods: {
    isNullOrEmpty ( str ) {
      return str == null || str === "";
    },
    focusInstanceNameField () {
      this.$refs.instanceNameField.$el.focus();
    },
    async loadInstanceNames () {
      try {
        switch ( this.instanceType ) {
          case this.INSTANCE_TYPE.THREE_SIXTY: {
            const { data: threeSixtyInstances } = await loadThreeSixtyInstances();
            this.instances = threeSixtyInstances.map( instance => instance.instanceName );
            break;
          }
          case this.INSTANCE_TYPE.PERSONA: {
            const { data: personaInstances } = await loadPersonaInstances();
            this.instances = personaInstances.map( instance => instance.instanceName );
            break;
          }
          case this.INSTANCE_TYPE.STYLE: {
            const { data: styleInstances } = await loadStyleInstances();
            this.instances = styleInstances.map( instance => instance.instanceName );
            break;
          }
          case this.INSTANCE_TYPE.SURVEY: {
            const { data: surveyInstances } = await loadSurveyInstances();
            this.instances = surveyInstances.map( instance => {
              const { surveyName } = instance;
              return surveyName;
            } );
            break;
          }
          case this.INSTANCE_TYPE.ENGAGE: {
            const { data: engageInstances } = await loadEngageInstances();
            this.instances = engageInstances.map( instance => instance.instanceName );
            break;
          }
          case this.INSTANCE_TYPE.TEAM: {
            const { data: teamInstances } = await loadTeamInstances();
            this.instances = teamInstances.map( instance => instance.instanceName );
            break;
          }
          default:
            break;
        }
      } catch ( error ) {
        console.warn( error );
      }
    },
    submitThreeSixtyInstance () {
      // const now = new Date( Date.now() ).toJSON();
      const payload = {
        instanceId: null,
        instanceName: this.instanceName,
        dateSubmitted: null,
        pathwayItemId: null,
      };

      this.ui.instance.loading = true;
      this.ui.next.disabled = true;

      saveThreeSixtyInstance( payload )
        .then( response => {
          const { status, data: instanceId } = response;

          if ( status === 200 && this.isNullOrEmpty( instanceId ) === false ) {
            this.currentInstanceId = instanceId;
            this.$emit( "success", this.currentInstanceId );
            window.location.href = `${this.next}?id=${instanceId}`;
          } else {
            throw new Error( "Instance failed to be created" );
          }
        } )
        .catch( ex => {
          console.error( ex );
          this.ui.instance.error = true;
          this.ui.next.disabled = false;
        } )
        .finally( () => {
          this.ui.instance.loading = false;
        } );
    },
    submitPersonaInstance () {
      const payload = {
        instanceId: null,
        instanceName: this.instanceName,
        pathwayItemId: null,
      };

      this.ui.instance.loading = true;
      this.ui.next.disabled = true;

      savePersonaInstance( payload )
        .then( response => {
          const { status, data: instanceId } = response;

          if ( status === 200 && this.isNullOrEmpty( instanceId ) === false ) {
            this.currentInstanceId = instanceId;
            this.$emit( "success", this.currentInstanceId );
            window.location.href = `${this.next}?id=${instanceId}`;
          } else {
            throw new Error( "Instance failed to be created" );
          }
        } )
        .catch( ex => {
          console.error( ex );
          this.ui.instance.error = true;
          this.ui.next.disabled = false;
        } )
        .finally( () => {
          this.ui.instance.loading = false;
        } );
    },
    submitStyleInstance () {
      const payload = {
        instanceId: null,
        instanceName: this.instanceName,
        pathwayItemId: null,
      };

      this.ui.instance.loading = true;
      this.ui.next.disabled = true;

      saveStyleInstance( payload )
        .then( response => {
          const { status, data: instanceId } = response;

          if ( status === 200 && this.isNullOrEmpty( instanceId ) === false ) {
            this.currentInstanceId = instanceId;
            this.$emit( "success", this.currentInstanceId );
            window.location.href = `${this.next}?id=${instanceId}`;
          } else {
            throw new Error( "Instance failed to be created" );
          }
        } )
        .catch( ex => {
          console.error( ex );
          this.ui.instance.error = true;
          this.ui.next.disabled = false;
        } )
        .finally( () => {
          this.ui.instance.loading = false;
        } );
    },
    submitSurveyInstance () {
      const defaultClosingDate = new Date();
      defaultClosingDate.setDate( defaultClosingDate.getDate() + this.defaultSurveyExpiryDays );

      const payload = {
        instanceId: null,
        surveyName: this.instanceName,
        surveyType: this.surveyType,
        organisationName: null,
        organisationLogo: null,
        closingDateTime: defaultClosingDate.toJSON(),
        pathwayItemId: null,
      };

      this.ui.instance.loading = true;
      this.ui.next.disabled = true;

      saveSurveyInstance( payload )
        .then( response => {
          const { status, data: instanceId } = response;

          if ( status === 200 && this.isNullOrEmpty( instanceId ) === false ) {
            this.currentInstanceId = instanceId;
            this.$emit( "success", this.currentInstanceId );
            window.location.href = `${this.next}?id=${instanceId}`;
          } else {
            throw new Error( "Instance failed to be created" );
          }
        } )
        .catch( ex => {
          console.error( ex );
          this.ui.instance.error = true;
          this.ui.next.disabled = false;
        } )
        .finally( () => {
          this.ui.instance.loading = false;
        } );
    },
    submitEngageInstance () {
      // const now = new Date( Date.now() ).toJSON();
      const payload = {
        instanceId: null,
        instanceName: this.instanceName,
        dateSubmitted: null,
        pathwayItemId: null,
      };

      this.ui.instance.loading = true;
      this.ui.next.disabled = true;

      saveEngageInstance( payload )
        .then( response => {
          const { status, data: instanceId } = response;

          if ( status === 200 && this.isNullOrEmpty( instanceId ) === false ) {
            this.currentInstanceId = instanceId;
            this.$emit( "success", this.currentInstanceId );
            window.location.href = `${this.next}?id=${instanceId}`;
          } else {
            throw new Error( "Instance failed to be created" );
          }
        } )
        .catch( ex => {
          console.error( ex );
          this.ui.instance.error = true;
          this.ui.next.disabled = false;
        } )
        .finally( () => {
          this.ui.instance.loading = false;
        } );
    },
    submitTeamInstance () {
      // const now = new Date( Date.now() ).toJSON();
      const payload = {
        instanceId: null,
        instanceName: this.instanceName,
        dateSubmitted: null,
        pathwayItemId: null,
      };

      this.ui.instance.loading = true;
      this.ui.next.disabled = true;

      saveTeamInstance( payload )
        .then( response => {
          const { status, data: instanceId } = response;

          if ( status === 200 && this.isNullOrEmpty( instanceId ) === false ) {
            this.currentInstanceId = instanceId;
            this.$emit( "success", this.currentInstanceId );
            window.location.href = `${this.next}?id=${instanceId}`;
          } else {
            throw new Error( "Instance failed to be created" );
          }
        } )
        .catch( ex => {
          console.error( ex );
          this.ui.instance.error = true;
          this.ui.next.disabled = false;
        } )
        .finally( () => {
          this.ui.instance.loading = false;
        } );
    },
    onNext () {
      if ( this.isNullOrEmpty( this.instanceName ) ) {
        this.userInputDetected = true;
        return;
      }

      switch ( this.instanceType ) {
        case this.INSTANCE_TYPE.THREE_SIXTY:
          this.submitThreeSixtyInstance();
          break;
        case this.INSTANCE_TYPE.PERSONA:
          this.submitPersonaInstance();
          break;
        case this.INSTANCE_TYPE.STYLE:
          this.submitStyleInstance();
          break;
        case this.INSTANCE_TYPE.SURVEY:
          this.submitSurveyInstance();
          break;
        case this.INSTANCE_TYPE.ENGAGE:
          this.submitEngageInstance();
          break;
        case this.INSTANCE_TYPE.TEAM:
          this.submitTeamInstance();
          break;
        default:
          window.location.href = "/error";
      }
    },
    onPrev () {
      window.history.back();
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../../Styles/utils/index.less";

.instance-name-field {
  &__container {
    margin: 30px 0;
    .border-radius(4px);
    background: #fff;
    box-shadow: 0 3px 20px rgba(0, 0, 0, 0.1);
    padding: 20px 28px 48px;
    position: relative;
  }

  &__background {
    .opacity-background(@light-blue, 0.8);
  }

  &__title {
    color: @blue;
    font-size: 1.2em;
    margin: 0 0 16px 0;
  }

  &__asterix {
    color: @required;
  }

  &__input {
    .border-radius(0px);
    border: 1px solid @blue;

    &__title {
      margin: 0;
    }
  }
}
</style>
